import AgoraRtc from "agora-rtc-sdk-ng";

// ...

let uid;

let rtcClient;

let subscriptions = {};

let listeners = {};

// note: Audience members publish audio tracks only.
let localTrack;

// ...

function onEvent(event, ...args) {
  const callbacks = listeners[event];
  if (callbacks) {
    callbacks.forEach(callback => callback(...args));
  }
}

// ...

function join(channelId, token, listeners) {
  // Specify the region for connection 
  AgoraRtc.setArea({
    areaCode: process.env.REACT_APP_RTC_AREA_CODE
  })

  rtcClient = AgoraRtc.createClient({ mode: 'rtc', codec: 'vp8' });
  rtcClient.setEncryptionConfig("aes-256-xts", process.env.REACT_APP_TOKEN_SECRET);

  Object.entries(listeners).forEach(([eventName, listener]) => rtcClient.on(eventName, listener));

  // ...

  rtcClient.on('user-published', async (user, mediaType) => {
    await rtcClient.subscribe(user, mediaType);

    // ...

    const uid = user.uid;

    if (!subscriptions[uid]) {
      subscriptions[uid] = {};
    }

    if (mediaType === 'video') {
      subscriptions[uid].video = user.videoTrack;

      onEvent('user-published', uid, 'video');
    }

    if (mediaType === 'audio') {
      subscriptions[uid].audio = user.audioTrack;

      onEvent('user-published', uid, 'audio');
    }
  });

  rtcClient.on('user-unpublished', (user, mediaType) => {
    delete subscriptions[user.uid][mediaType];

    onEvent('user-unpublished', user.uid, mediaType);
  });

  // ...

  return rtcClient.join(process.env.REACT_APP_AGORA_APP_ID, channelId, token, uid);
}

function leave() {
  if (localTrack) {
    localTrack.stop();
    localTrack.close();

    localTrack = null;
  }

  // ...

  Object.values(subscriptions).forEach(({ audio, video }) => {
    if (audio) {
      audio.stop();
    }

    if (video) {
      video.stop();
    }
  });

  subscriptions = {};
  listeners = {};

  // ...

  if (rtcClient) {
    rtcClient.leave();
    rtcClient = null;
  }
}

function onUserPublished(listener) {
  if (!listeners['user-published']) {
    listeners['user-published'] = [];
  }

  listeners['user-published'].push(listener);

  for (const uid in subscriptions) {
    const {
      audio,
      video
    } = subscriptions[uid];

    if (video) {
      listener(uid, 'video');
    }

    if (audio) {
      listener(uid, 'audio');
    }
  }
}

function onUserUnpublished(listener) {
  if (!listeners['user-unpublished']) {
    listeners['user-unpublished'] = [];
  }

  listeners['user-unpublished'].push(listener);
}

function hasRemoteAudio(uid) {
  return !!subscriptions[uid]?.audio;
}

function hasRemoteVideo(uid) {
  return !!subscriptions[uid]?.video;
}

function playRemoteAudio(uid) {
  const track = subscriptions[uid]?.audio;
  if (!track) {
    return;
  }

  if (!track.isPlaying) {
    track.play();
  }
}

function playRemoteVideo(uid, container) {
  const track = subscriptions[uid]?.video;
  if (!track) {
    return;
  }

  if (!track.isPlaying) {
    track.play(container);
  }
}

function stopRemoteVideo(uid) {
  const track = subscriptions[uid]?.video;
  if (!track) {
    return;
  }

  if (track.isPlaying) {
    track.stop();
  }
}

async function playAudio() {
  if (localTrack) {
    return;
  }

  localTrack = await AgoraRtc.createMicrophoneAudioTrack();

  rtcClient.publish(localTrack);
}

function setMute(shouldMute) {
  if (localTrack) {
    const volume = shouldMute ? 0 : 100;
    localTrack.setVolume(volume);
  }
}

function setUid(_uid) {
  uid = _uid;
}

function getUid() {
  return uid;
}

// ...

const rtc = {
  setUid,
  getUid,

  join,
  leave,

  onUserPublished,
  onUserUnpublished,

  hasRemoteAudio,
  hasRemoteVideo,

  playRemoteAudio,
  playRemoteVideo,
  stopRemoteVideo,

  playAudio,
  setMute
};

export default rtc;